import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, Typography, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, Divider, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell } from 'recharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { getComponentDescription } from '../utils/SankeyComponentDescriptions';

const currencySymbols = {
  USD: '$',
  JPY: '¥',
  EUR: '€',
  GBP: '£',
  CNY: '¥',
  TWD: 'NT$',
  KRW: '₩',
  INR: '₹',
  BRL: 'R$',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'Fr',
  RMB: '¥',
};

const getCurrencySymbol = (curr) => currencySymbols[curr] || curr || '$';

const formatValue = (value, currencySymbol = '$') => {
  if (!Number.isFinite(Number(value))) return `${currencySymbol}0`;
  const numeric = Number(value);
  const abs = Math.abs(numeric);

  if (abs >= 1_000_000_000) return `${currencySymbol}${(numeric / 1_000_000_000).toPrecision(3)}B`;
  if (abs >= 1_000_000) return `${currencySymbol}${(numeric / 1_000_000).toPrecision(3)}M`;
  if (abs >= 1_000) return `${currencySymbol}${(numeric / 1_000).toPrecision(3)}K`;
  return `${currencySymbol}${numeric.toPrecision(3)}`;
};

const findNode = (nodes, candidateNames) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return null;
  const lowered = candidateNames.map((name) => String(name).toLowerCase());
  return nodes.find((node) => lowered.includes(String(node?.name || '').toLowerCase())) || null;
};

const getNodeValue = (node) => {
  if (node?.value === null || node?.value === undefined || node?.value === '') return null;
  const value = Number(node.value);
  return Number.isFinite(value) ? value : null;
};

const getSignedTotal = (node, lossPattern) => {
  const value = getNodeValue(node);
  if (value === null) return null;
  return lossPattern.test(String(node?.name || '')) ? -Math.abs(value) : value;
};

export const buildWaterfallRows = (nodes) => {
  const revenueNode = findNode(nodes, ['Revenue']);
  const costNode = findNode(nodes, ['Cost of Revenue']);
  const grossProfitNode = findNode(nodes, ['Gross Profit', 'Gross Loss']);
  const operatingExpensesNode = findNode(nodes, ['Operating Expenses']);
  const operatingExpenseDetails = nodes.filter((node) => node?.isOperatingExpenseDetail === true);
  const operatingNode = findNode(nodes, ['Operating Income', 'Operating Loss']);
  const otherIncomeDetails = nodes.filter((node) => node?.isOtherIncomeDetail === true);
  const interestNode = findNode(nodes, ['Interest Expense', 'Interest Income']);
  const preTaxNode = findNode(nodes, ['Income Before Tax', 'Loss Before Tax']);
  const taxNode = findNode(nodes, ['Income Tax', 'Income Tax Benefit']);
  const netNode = findNode(nodes, ['Net Income', 'Net Loss']);

  const rows = [];
  let runningTotal = 0;

  const pushDelta = (label, delta, kind) => {
    if (!Number.isFinite(delta) || delta === 0) return;
    const start = runningTotal;
    const end = runningTotal + delta;
    rows.push({
      label,
      kind,
      delta,
      cumulative: end,
      offset: Math.min(start, end),
      amount: Math.abs(delta),
    });
    runningTotal = end;
  };

  const pushTotal = (label, total, kind) => {
    if (!Number.isFinite(total)) return;
    rows.push({
      label,
      kind,
      delta: total - runningTotal,
      cumulative: total,
      offset: Math.min(0, total),
      amount: Math.abs(total),
      isTotal: true,
    });
    runningTotal = total;
  };

  const revenue = getNodeValue(revenueNode);
  if (revenue !== null) {
    pushTotal('Revenue', revenue, 'income');
  }

  const reportedCostOfRevenue = getNodeValue(costNode);
  const reportedGrossProfit = getSignedTotal(grossProfitNode, /loss/i);
  const hasPlaceholderGrossProfitSection =
    revenue !== null &&
    reportedCostOfRevenue !== null &&
    (reportedGrossProfit === null || Math.abs(reportedGrossProfit) <= Math.max(1, Math.abs(revenue)) * 0.005) &&
    Math.abs(Math.abs(reportedCostOfRevenue) - Math.abs(revenue)) <= Math.max(1, Math.abs(revenue)) * 0.005;
  const grossProfitValue = hasPlaceholderGrossProfitSection
    ? null
    : reportedGrossProfit !== null
    ? reportedGrossProfit
    : (revenue !== null && reportedCostOfRevenue !== null
      ? revenue - Math.abs(reportedCostOfRevenue)
      : null);
  if (grossProfitValue !== null) {
    if (revenue !== null) {
      pushDelta(
        'Cost of Revenue',
        grossProfitValue - runningTotal,
        grossProfitValue - runningTotal >= 0 ? 'income' : 'expense'
      );
    }
    pushTotal('Gross Profit', grossProfitValue, grossProfitValue >= 0 ? 'income' : 'expense');
  }

  const reportedOperatingExpenses = getNodeValue(operatingExpensesNode);
  const signedOperatingValue = getSignedTotal(operatingNode, /loss/i);
  if (signedOperatingValue !== null) {
    if (grossProfitValue !== null || revenue !== null) {
      operatingExpenseDetails.forEach((node) => {
        const value = getNodeValue(node);
        if (value !== null) {
          const signedValue = node.lossPositive ? -value : -Math.abs(value);
          pushDelta(String(node.name), signedValue, signedValue >= 0 ? 'income' : 'expense');
        }
      });
      const operatingDelta = signedOperatingValue - runningTotal;
      if (operatingDelta !== 0) {
        pushDelta(
          operatingDelta > 0
            ? (operatingExpenseDetails.length ? 'Expense Classification Adjustment' : 'Operating Adjustments')
            : (operatingExpenseDetails.length
              ? 'Other Operating Expenses'
              : (grossProfitValue !== null ? 'Operating Expenses' : 'Operating Costs')),
          operatingDelta,
          operatingDelta >= 0 ? 'income' : 'expense'
        );
      }
    } else if (reportedOperatingExpenses !== null) {
      pushDelta('Operating Expenses', -Math.abs(reportedOperatingExpenses), 'expense');
    }
    pushTotal(String(operatingNode?.name || 'Operating Income'), signedOperatingValue, signedOperatingValue >= 0 ? 'income' : 'expense');
  } else if (reportedOperatingExpenses !== null) {
    pushDelta('Operating Expenses', -Math.abs(reportedOperatingExpenses), 'expense');
  }

  const interestValue = getNodeValue(interestNode);
  if (interestValue !== null) {
    const signedInterestValue = /income/i.test(String(interestNode?.name || ''))
      ? Math.abs(interestValue)
      : -Math.abs(interestValue);
    pushDelta(String(interestNode?.name || 'Interest'), signedInterestValue, signedInterestValue >= 0 ? 'income' : 'expense');
  }

  otherIncomeDetails.forEach((node) => {
    const value = getNodeValue(node);
    if (value === null) return;
    const signedValue = node.lossPositive ? -value : value;
    pushDelta(String(node.name), signedValue, signedValue >= 0 ? 'income' : 'expense');
  });

  const signedNetValue = getSignedTotal(netNode, /loss/i);
  const taxValue = getNodeValue(taxNode);
  const signedTaxValue = taxValue === null
    ? null
    : (/benefit/i.test(String(taxNode?.name || '')) ? Math.abs(taxValue) : -Math.abs(taxValue));
  const reportedPreTaxValue = getSignedTotal(preTaxNode, /loss/i);
  const signedPreTaxValue = reportedPreTaxValue !== null
    ? reportedPreTaxValue
    : (signedNetValue !== null && signedTaxValue !== null ? signedNetValue - signedTaxValue : null);
  if (signedPreTaxValue !== null) {
    const reconciliationDelta = signedPreTaxValue - runningTotal;
    if (reconciliationDelta !== 0) {
      pushDelta('Other Items', reconciliationDelta, reconciliationDelta >= 0 ? 'income' : 'expense');
    }
    pushTotal(String(preTaxNode?.name || 'Income Before Tax'), signedPreTaxValue, signedPreTaxValue >= 0 ? 'income' : 'expense');
  }

  if (signedTaxValue !== null) {
    pushDelta(String(taxNode?.name || 'Income Tax'), signedTaxValue, signedTaxValue >= 0 ? 'income' : 'expense');
  }

  if (signedNetValue !== null) {
    const reconciliationDelta = signedNetValue - runningTotal;
    if (reconciliationDelta !== 0) {
      pushDelta('Other Items', reconciliationDelta, reconciliationDelta >= 0 ? 'income' : 'expense');
    }

    pushTotal(String(netNode?.name || 'Net Income'), signedNetValue, signedNetValue >= 0 ? 'income' : 'expense');
  }

  return rows;
};

const IncomeBridgeWaterfall = ({ data, title, loading = false, currency = 'USD' }) => {
  const theme = useTheme();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const currencySymbol = getCurrencySymbol(currency);

  const rows = useMemo(() => {
    const nodes = Array.isArray(data?.nodes) ? data.nodes : [];
    return buildWaterfallRows(nodes);
  }, [data]);

  const colorByKind = {
    income: '#10b981',
    expense: '#ef4444',
  };

  const labelHeaders = Array.from(new Set(rows.map((row) => row.label).filter(Boolean)));

  const getSectionColor = (label) => {
    const matchedRow = rows.find((row) => row.label === label);
    if (!matchedRow) return theme.palette.primary.main;
    const sectionValue = matchedRow.isTotal ? Number(matchedRow.cumulative) : Number(matchedRow.delta);
    if (!Number.isFinite(sectionValue)) return theme.palette.primary.main;
    return sectionValue >= 0 ? theme.palette.success.main : theme.palette.error.main;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column">
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box height="100%">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          {title}
        </Typography>
        <MuiTooltip title="View waterfall component descriptions">
          <IconButton
            size="small"
            onClick={() => setInfoDialogOpen(true)}
            sx={{ p: 0, minWidth: '24px', width: '24px', height: '24px' }}
          >
            <InfoOutlinedIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </MuiTooltip>
      </Box>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Waterfall Components</Typography>
          <IconButton
            onClick={() => setInfoDialogOpen(false)}
            size="small"
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ overflowY: 'auto', py: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {labelHeaders.map((label) => {
              const componentInfo = getComponentDescription(label, 'income');
              const likelySubComponents = componentInfo?.subComponents || [
                'See company filings for detailed line items',
                'Typical composition varies by sector and business model',
              ];
              const sectionLabelColor = getSectionColor(label);

              return (
                <Box key={label} sx={{ pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none' } }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: sectionLabelColor, mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 0.5, color: theme.palette.text.secondary }}>
                    Typical Sub-components:
                  </Typography>
                  <List sx={{ py: 0, ml: 1 }}>
                    {likelySubComponents.map((subComponent, idx) => (
                      <ListItem key={idx} sx={{ py: 0.25, px: 0, pl: 1 }}>
                        <ListItemText
                          primary={subComponent}
                          primaryTypographyProps={{
                            variant: 'caption',
                            sx: { color: theme.palette.text.primary },
                          }}
                          sx={{ my: 0 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      <Box sx={{ height: '90%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            margin={{ top: 20, right: 24, left: 8, bottom: 56 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis
              dataKey="label"
              interval={0}
              angle={-25}
              textAnchor="end"
              height={60}
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            />
            <YAxis
              tickFormatter={(value) => formatValue(value, currencySymbol)}
              tick={{ fill: theme.palette.text.secondary }}
              width={92}
            />
            <ReferenceLine y={0} stroke={theme.palette.text.secondary} strokeOpacity={0.45} />
            <Tooltip
              formatter={(value, name, entry) => {
                const payload = entry?.payload || {};
                if (name === 'amount') {
                  if (payload.isTotal) {
                    return [formatValue(payload.cumulative, currencySymbol), `${payload.label} Total`];
                  }
                  return [formatValue(payload.delta, currencySymbol), `${payload.label} Change`];
                }
                if (name === 'offset') {
                  if (payload.isTotal) {
                    return [formatValue(payload.cumulative, currencySymbol), `${payload.label} Total`];
                  }
                  return [formatValue(payload.cumulative, currencySymbol), `${payload.label} Running Total`];
                }
                return [formatValue(value, currencySymbol), name];
              }}
            />
            <Bar dataKey="offset" stackId="waterfall" fill="transparent" isAnimationActive={false} />
            <Bar dataKey="amount" stackId="waterfall" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {rows.map((row, idx) => {
                const label = String(row?.label || '');
                const isNetIncomeTotal = /net\s+(income|loss)/i.test(label);
                // Net Income should read as profit (green), not as a blue total bar.
                const fill = row.isTotal
                  ? (row.cumulative >= 0
                    ? (isNetIncomeTotal ? colorByKind.income : '#3b82f6')
                    : '#ef4444')
                  : (row.delta >= 0 ? colorByKind.income : colorByKind.expense);
                return <Cell key={`${row.label}-${idx}`} fill={fill} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default IncomeBridgeWaterfall;